import { Row } from "./row";
import { EntryType } from "../Compo/CompoPage";

export type CompoType = "main" | "day" | "hour" | "meme";

export function secondsToString(ms: number): string {
  ms = Math.abs(ms);

  const seconds = Math.floor(ms / 1000);
  const numyears = Math.floor(seconds / 31536000);
  const numdays = Math.floor((seconds % 31536000) / 86400);
  const numhours = Math.floor(((seconds % 31536000) % 86400) / 3600);
  const numminutes = Math.floor((((seconds % 31536000) % 86400) % 3600) / 60);
  const numseconds = (((seconds % 31536000) % 86400) % 3600) % 60;

  let parts: string[] = [];
  let isBig = false;

  if (numyears > 0) {
    parts.push(` ${numyears} year${numyears === 1 ? "" : "s"}`);
    isBig = true;
  }
  if (numdays > 0) {
    parts.push(` ${numdays} day${numdays === 1 ? "" : "s"}`);
    isBig = true;
  }
  if (numhours > 0) {
    parts.push(` ${numhours} hour${numhours === 1 ? "" : "s"}`);
    isBig = true;
  }

  if (numminutes > 0) {
    parts.push(` ${numminutes} minute${numminutes === 1 ? "" : "s"}`);
  }
  if (numseconds > 0 && !isBig) {
    parts.push(` ${numseconds} second${numseconds === 1 ? "" : "s"}`);
  }

  return parts.join(", ");
}

export interface CompoJSON {
  id: number;

  type: CompoType;
  description: string;
  judging_stop: string | Date;
  header_image: string;
  name: string;
  judged: boolean;
  primary_image: string;

  /**
   * Does the compo allows theme voting.
   */
  allow_voting: boolean;
  released: boolean;

  /**
   * This is a date string.
   */
  start: Date | string;

  /**
   * This is a date string.
   */
  stop: Date | string;
  types: EntryType[];
  nogold: boolean;
  theme: string;
  entries: number;
  theme_short: string;
  sent_xp: boolean;
  sent_gold: boolean;
  sent_trophies: boolean;
  hide_images_before_compo_start: boolean;
  is_secret_santa: boolean;
}

export class Compo extends Row<CompoJSON> implements CompoJSON {
  // Note: Most properties come from Row<CompoJSON> via the database
  // Only declare properties that are computed or modified in this class
  declare description: string;
  declare judging_stop: string | Date;
  declare name: string;
  declare primary_image: string;
  declare header_image: string;
  declare allow_voting: boolean;
  declare released: boolean;
  declare sent_xp: boolean;
  declare sent_gold: boolean;
  declare sent_trophies: boolean;
  declare judged: boolean;
  declare types: EntryType[];
  declare nogold: boolean;
  declare theme: string;
  declare entries: number;
  declare theme_short: string;
  declare type: CompoType;
  declare start: Date | string;
  declare stop: Date | string;
  declare id: number;
  declare hide_images_before_compo_start: boolean;
  declare is_secret_santa: boolean;

  startDate: Date;
  stopDate: Date;
  judgingStopDate: Date;

  state: "before" | "entry" | "judging" | "over";

  /** Original values from the database, before any modifications */

  // TODO: This is kind of stupid, because now anyone can see them (if they read
  // the API, which no one does, but I digress). We should be doing the
  // modification to keep the images hidden on the backend, not the frontend We
  // need these for the compo admin page. But that's an insanely large lift.
  original_primary_image: string;
  original_header_image: string;

  constructor(props: CompoJSON) {
    super(props);

    this.original_primary_image = this.primary_image;
    this.original_header_image = this.header_image;

    this.startDate =
      this.start instanceof Date ? this.start : new Date(this.start);
    this.stopDate = this.stop instanceof Date ? this.stop : new Date(this.stop);
    this.judging_stop =
      this.judging_stop instanceof Date
        ? this.judging_stop
        : new Date(this.judging_stop);
    this.judgingStopDate =
      this.judging_stop instanceof Date
        ? this.judging_stop
        : new Date(this.judging_stop);

    const now = new Date();

    if (now > this.judging_stop && this.released) {
      this.state = "over";
    } else if (now > this.stopDate) {
      this.state = "judging";
    } else if (now > this.startDate) {
      this.state = "entry";
    } else {
      this.state = "before";
      // Only hide images on the client side - server should return original values
      // so that toJSON() serializes the real image URLs
      if (this.hide_images_before_compo_start && typeof window !== "undefined") {
        this.header_image = "/img/default-header.jpg";
        this.primary_image = "/img/unknown.png";
      }
    }
  }

  get linkToCompo(): string {
    return "/compo/" + this.id;
  }

  get linkToCompoResults(): string {
    return "/compo/" + this.id + "/results";
  }

  get nextEvent(): { event: string; time: Date } {
    if (this.state === "before") {
      return {
        event: "Competition starts",
        time: this.startDate,
      };
    }

    if (this.state === "entry") {
      return {
        event: "Submit a song",
        time: this.stopDate,
      };
    }

    if (this.state === "judging") {
      return {
        event: "Finish voting",
        time: this.judgingStopDate,
      };
    }

    return {
      event: "Results are announced",
      time: new Date(),
    };
  }
}
